import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const load = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const lineKey = (p) => `${p.product_id}|${p.color || ""}|${p.size || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load("adams_cart", []));
  const [wishlist, setWishlist] = useState(() => load("adams_wishlist", []));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => localStorage.setItem("adams_cart", JSON.stringify(items)), [items]);
  useEffect(() => localStorage.setItem("adams_wishlist", JSON.stringify(wishlist)), [wishlist]);

  const addToCart = useCallback((product, { color, size, quantity = 1, image } = {}, silent = false) => {
    const entry = {
      product_id: product.id,
      slug: product.slug,
      name: product.name,
      image: image || product.images?.[0] || "",
      price: product.discount_price || product.price,
      color: color ?? product.colors?.[0] ?? null,
      size: size ?? product.sizes?.[0] ?? null,
      quantity,
    };
    setItems((prev) => {
      const idx = prev.findIndex((i) => lineKey(i) === lineKey(entry));
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, entry];
    });
    if (!silent) {
      toast.success("Added to bag", { description: product.name });
      setDrawerOpen(true);
    }
  }, []);

  const updateQty = useCallback((key, qty) =>
    setItems((prev) => prev.map((i) => (lineKey(i) === key ? { ...i, quantity: Math.max(1, qty) } : i))), []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => lineKey(i) !== key)), []);
  const clearCart = useCallback(() => setItems([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) {
        toast("Removed from wishlist");
        return prev.filter((p) => p.id !== product.id);
      }
      toast.success("Added to wishlist", { description: product.name });
      return [...prev, { id: product.id, slug: product.slug, name: product.name, image: product.images?.[0], price: product.discount_price || product.price, discount_price: product.discount_price, colors: product.colors, sizes: product.sizes, images: product.images }];
    });
  }, []);

  const inWishlist = useCallback((id) => wishlist.some((p) => p.id === id), [wishlist]);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);

  const value = useMemo(() => ({
    items, addToCart, updateQty, removeItem, clearCart, count, subtotal, lineKey,
    wishlist, toggleWishlist, inWishlist, drawerOpen, setDrawerOpen, menuOpen, setMenuOpen,
  }), [items, addToCart, updateQty, removeItem, clearCart, count, subtotal, wishlist, toggleWishlist, inWishlist, drawerOpen, menuOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
